import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Images } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { useLang, whatsappLink } from "../i18n";
import { Reveal, Eyebrow, MaskedLine } from "../components/Reveal";
import { ParallaxImage } from "../components/ParallaxImage";
import EditorialProject from "../components/EditorialProject";
import EditorialBreak from "../components/EditorialBreak";
import BeforeAfter from "../components/BeforeAfter";
import { WhatsAppIcon } from "../components/WhatsAppButton";

const HERO = "arte-infissi/images/a619a8f0-a2f0-4ac2-9b5d-b7b77554b06e.webp";
const STAIR = "arte-infissi/images/0ee7b7cd-f236-4837-bbf5-240b9652e656.webp";

const COPY = {
  it: {
    eyebrow: "Balaustre in Vetro",
    title: "Il vuoto, protetto.",
    sub: "Parapetti in vetro temperato stratificato che proteggono senza interrompere lo sguardo: terrazze, scale e piscine restano aperte sul paesaggio.",
    intro: "Una balaustra in vetro non si guarda: si guarda attraverso. Progettiamo e installiamo parapetti su misura in vetro stratificato di sicurezza, con o senza profilo in alluminio, per terrazzi, balconi, scale interne e bordi piscina. Ogni sistema è certificato per la spinta e dimensionato sul tuo progetto.",
    break_title: "Anche in scala.",
    break_line: "Lo stesso vetro che protegge la terrazza accompagna i gradini: un solo piano trasparente, dal giardino al tetto.",
    systems_eyebrow: "I sistemi",
    systems: [
      ["Vetro autoportante", "Lastre stratificate 8+8 o 10+10 mm fissate a pavimento con profilo continuo incassato: nessun montante, nessun corrimano. La forma più pura del parapetto."],
      ["Con struttura in alluminio", "Montanti e corrimano in alluminio verniciato, in tinta con gli infissi: robustezza e continuità estetica con finestre e vetrate della casa."],
      ["Installazioni su misura", "Bordi piscina, scale a sbalzo, terrazze curve, fissaggi laterali su solette esistenti: rilievo in cantiere e realizzazione dedicata per ogni geometria."],
    ],
    gallery_eyebrow: "Realizzazioni",
    gallery_title: "Terrazze che finiscono nel paesaggio.",
    gallery_empty_title: "Galleria in preparazione",
    gallery_empty_sub: "Le fotografie delle balaustre in vetro realizzate saranno pubblicate qui. Carica i progetti dall'area riservata con categoria Balaustre in Vetro.",
    ba_eyebrow: "Prima / Dopo",
    cta_title: "Un parapetto che non toglie nulla alla vista.",
    cta: "Richiedi un preventivo",
  },
  en: {
    eyebrow: "Glass Balustrades",
    title: "The void, protected.",
    sub: "Laminated tempered glass railings that protect without interrupting the view: terraces, stairs and pools stay open to the landscape.",
    intro: "A glass balustrade is not something you look at: you look through it. We design and install bespoke railings in laminated safety glass, with or without aluminium profiles, for terraces, balconies, interior stairs and pool edges. Every system is load-certified and sized for your project.",
    break_title: "On the stairs too.",
    break_line: "The same glass that protects the terrace follows the steps: one transparent plane, from garden to roof.",
    systems_eyebrow: "The systems",
    systems: [
      ["Frameless glass", "8+8 or 10+10 mm laminated panes fixed to the floor with a continuous recessed profile: no posts, no handrail. The purest form of railing."],
      ["Aluminium-supported", "Painted aluminium posts and handrail, matching the windows: strength and aesthetic continuity with the home's frames and glass walls."],
      ["Custom installations", "Pool edges, cantilevered stairs, curved terraces, side fixings on existing slabs: on-site survey and dedicated fabrication for every geometry."],
    ],
    gallery_eyebrow: "Works",
    gallery_title: "Terraces that end in the landscape.",
    gallery_empty_title: "Gallery in preparation",
    gallery_empty_sub: "Photographs of completed glass balustrades will be published here. Upload projects from the admin area with category Glass Balustrades.",
    ba_eyebrow: "Before / After",
    cta_title: "A railing that takes nothing away from the view.",
    cta: "Request a quote",
  },
};

export default function Balaustre() {
  const { lang } = useLang();
  const c = COPY[lang];
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects", { params: { category: "railings" } }).then(({ data }) => setProjects(data)).catch(() => {});
  }, []);
  const ba = projects.find((p) => p.before_image);

  return (
    <div data-testid="balaustre-page">
      <section className="relative isolate h-[100svh] min-h-[600px] flex flex-col justify-end overflow-hidden">
        <ParallaxImage src={HERO} alt={c.eyebrow} className="absolute inset-0 -z-10" strength={10} loading="eager" position="center" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-[#080808]/20" />
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 data-testid="balaustre-title" className="mt-6 font-serif font-light tracking-tight leading-[0.98] text-[#F3EFE6] text-5xl sm:text-6xl lg:text-8xl">
            <MaskedLine delay={0.2}>{c.title}</MaskedLine>
          </h1>
          <MaskedLine delay={0.45}>
            <p className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-[#D6D1C6]">{c.sub}</p>
          </MaskedLine>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4"><Eyebrow>{c.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p data-testid="balaustre-intro" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.3] text-[#F3EFE6]">{c.intro}</p>
          </Reveal>
        </div>
      </section>

      <EditorialBreak testid="balaustre-break" image={STAIR} headline={c.break_title} line={c.break_line} align="right" position="center 40%" />

      <section className="border-t border-[#1C1A17] bg-[#0A0A09]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
          <Reveal><Eyebrow>{c.systems_eyebrow}</Eyebrow></Reveal>
          <div className="mt-12">
            {c.systems.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div data-testid={`balaustre-system-${i}`} className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-t border-[#1C1A17] py-12 transition-colors duration-500 hover:bg-[#0F0F0E]">
                  <span className="md:col-span-2 font-mono text-4xl sm:text-5xl text-[#C8A86B]/60 transition-colors duration-500 group-hover:text-[#C8A86B]">0{i + 1}</span>
                  <h3 className="md:col-span-4 font-serif text-2xl sm:text-3xl font-light text-[#F3EFE6] leading-snug">{title}</h3>
                  <p className="md:col-span-6 text-sm sm:text-base font-light leading-relaxed text-[#A39E93]">{text}</p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-[#1C1A17]" />
          </div>
        </div>
      </section>

      <section data-testid="balaustre-gallery" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
        <Reveal>
          <Eyebrow>{c.gallery_eyebrow}</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{c.gallery_title}</h2>
        </Reveal>
        {projects.length > 0 ? (
          <div className="mt-20 flex flex-col gap-24 sm:gap-36">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={0.05}><EditorialProject project={p} index={i} /></Reveal>
            ))}
          </div>
        ) : (
          <Reveal className="mt-16">
            <div data-testid="balaustre-gallery-empty" className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1C1A17] border border-dashed border-[#C8A86B]/25">
              {[0, 1, 2].map((n) => (
                <div key={n} className="flex aspect-[4/5] md:aspect-[3/4] flex-col items-center justify-center gap-4 bg-[#080808] p-8 text-center">
                  <Images size={22} className="text-[#C8A86B]/60" />
                  {n === 1 && (
                    <>
                      <p className="font-serif text-xl text-[#F3EFE6]">{c.gallery_empty_title}</p>
                      <p className="max-w-xs text-xs font-light leading-relaxed text-[#827D72]">{c.gallery_empty_sub}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </section>

      {ba && (
        <section className="border-t border-[#1C1A17] bg-[#0A0A09]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
            <Reveal><Eyebrow>{c.ba_eyebrow}</Eyebrow></Reveal>
            <div className="mt-10"><BeforeAfter before={imgSrc(ba.before_image)} after={imgSrc(ba.cover_image)} alt={ba.title_it} /></div>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden border-t border-[#1C1A17]">
        <div className="absolute inset-0 bronze-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
          <Reveal>
            <h2 className="max-w-3xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{c.cta_title}</h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={whatsappLink("Buongiorno, vorrei un preventivo per una balaustra in vetro.")} target="_blank" rel="noopener noreferrer" data-testid="balaustre-quote-cta" className="inline-flex items-center gap-3 bg-[#C8A86B] px-8 py-4 text-sm uppercase tracking-[0.15em] text-[#080808] hover:bg-[#D8BD85]">
                <WhatsAppIcon size={16} /> {c.cta}
              </a>
              <Link to="/prodotti/vetrate-panoramiche" data-testid="balaustre-next-link" className="inline-flex items-center gap-3 border border-[#C8A86B] px-8 py-4 text-sm uppercase tracking-[0.15em] text-[#C8A86B] hover:bg-[#C8A86B] hover:text-[#080808]">
                Vetrate Panoramiche <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
